import { useState } from 'react';
import { ArrowDownToLine, ArrowUpFromLine } from 'lucide-react';
import type { OcppFrameRow } from '@/lib/api';
import styles from './PairedFrames.module.css';

/**
 * Shared OCPP frame pairing + row rendering.
 *
 * Frames from the backend arrive as individual CALL (type 2) +
 * CALLRESULT (3) / CALLERROR (4) records, paired by `message_id`.
 * Both the charger Logs tab and the per-session frame timeline on
 * SessionDetailPage want the same "request + matched response"
 * presentation, so the pairing math and row/body components live
 * here instead of being copy-pasted.
 */

export interface PairedFrame {
  messageId: string;
  request: OcppFrameRow;
  response?: OcppFrameRow;
}

/**
 * Group frames by message_id into request+response pairs, newest
 * request first. Defensive against missing message_ids so one bad
 * frame can't nuke the whole list.
 *
 * When frames arrive out-of-order (response before request, which
 * happens on live-tail edge cases), the first frame seats itself in
 * the `request` slot and the subsequent request gets swapped in
 * correctly.
 */
export function pairFrames(frames: OcppFrameRow[]): PairedFrame[] {
  const byId = new Map<string, PairedFrame>();
  for (const frame of frames) {
    if (!frame || typeof frame.message_id !== 'string') continue;
    const existing = byId.get(frame.message_id);
    const isResponse = frame.message_type === 3 || frame.message_type === 4;

    if (existing) {
      if (isResponse && !existing.response) {
        existing.response = frame;
      } else if (!isResponse && existing.request.message_type !== 2) {
        // The existing entry was a response in the request slot
        // (orphan anchor). Swap the response into its correct slot.
        existing.response = existing.request;
        existing.request = frame;
      }
    } else {
      byId.set(frame.message_id, { messageId: frame.message_id, request: frame });
    }
  }
  const out = Array.from(byId.values());
  out.sort((a, b) => {
    const at = a.request.timestamp ?? '';
    const bt = b.request.timestamp ?? '';
    return bt.localeCompare(at);
  });
  return out;
}

export function safeStringify(value: unknown): string {
  if (value === undefined) return '';
  try {
    return JSON.stringify(value) ?? '';
  } catch {
    return String(value);
  }
}

export function formatFrameTime(d: Date): string {
  if (Number.isNaN(d.getTime())) return '—';
  return (
    d.toLocaleTimeString(undefined, { hour12: false })
    + '.' + String(d.getMilliseconds()).padStart(3, '0')
  );
}

const BODY_PREVIEW_LEN = 200;

export function PairedRow({ pair }: { pair: PairedFrame }) {
  const [reqExpanded, setReqExpanded] = useState(false);
  const [resExpanded, setResExpanded] = useState(false);

  const isResponseError =
    pair.response?.message_type === 4 || Boolean(pair.response?.error_code);
  const Icon = pair.request.direction === 'incoming' ? ArrowDownToLine : ArrowUpFromLine;
  const reqTime = pair.request.timestamp ? new Date(pair.request.timestamp) : null;
  const resTime = pair.response?.timestamp ? new Date(pair.response.timestamp) : null;

  const reqBody = safeStringify(pair.request.payload);
  const resBody = pair.response
    ? safeStringify(pair.response.payload)
      + (pair.response.error_description ? `\n${pair.response.error_description}` : '')
    : '';

  return (
    <div className={`${styles.pairedRow} ${isResponseError ? styles.pairedRowErr : ''}`}>
      <div className={styles.pairedHead}>
        <span className={styles.frameTime}>
          {reqTime ? formatFrameTime(reqTime) : '—'}
        </span>
        <span className={`${styles.frameDir} ${pair.request.direction === 'incoming' ? styles.frameIn : styles.frameOut}`}>
          <Icon size={11} />
        </span>
        <span className={styles.frameAction}>{pair.request.action ?? '—'}</span>
        {pair.response?.error_code && (
          <span className={styles.frameErrCode}>{pair.response.error_code}</span>
        )}
        {!pair.response && (
          <span className={styles.pairedPending}>pending</span>
        )}
      </div>

      <BodyBlock
        label="Request"
        body={reqBody}
        expanded={reqExpanded}
        onToggle={() => setReqExpanded((e) => !e)}
      />

      {pair.response && (
        <BodyBlock
          label="Response"
          body={resBody}
          expanded={resExpanded}
          onToggle={() => setResExpanded((e) => !e)}
          time={resTime ? formatFrameTime(resTime) : undefined}
          isError={isResponseError}
        />
      )}
    </div>
  );
}

function BodyBlock({
  label, body, expanded, onToggle, time, isError,
}: {
  label: string;
  body: string;
  expanded: boolean;
  onToggle: () => void;
  time?: string;
  isError?: boolean;
}) {
  const needsTruncate = body.length > BODY_PREVIEW_LEN;
  const display = expanded || !needsTruncate ? body : body.slice(0, BODY_PREVIEW_LEN) + '…';
  return (
    <div className={`${styles.bodyBlock} ${isError ? styles.bodyBlockErr : ''}`}>
      <div className={styles.bodyLabel}>
        <span>{label}</span>
        {time && <span className={styles.bodyTime}>{time}</span>}
      </div>
      <pre className={styles.bodyPayload}>{display}</pre>
      {needsTruncate && (
        <button
          type="button"
          className={styles.bodyMore}
          onClick={onToggle}
        >
          {expanded ? 'less' : 'more'}
        </button>
      )}
    </div>
  );
}

/**
 * Convenience wrapper: renders a scrollable column of paired rows
 * with an empty-state slot. Consumers that need custom controls
 * (search, pause, filters) can skip this and map over `pairFrames()`
 * output with `<PairedRow>` directly.
 */
export function PairedFrameList({
  pairs,
  emptyLabel,
  maxHeight,
}: {
  pairs: PairedFrame[];
  emptyLabel?: string;
  maxHeight?: number | string;
}) {
  if (pairs.length === 0) {
    return (
      <div className={styles.framesList} style={maxHeight ? { maxHeight } : undefined}>
        <p className={styles.framesEmpty}>
          {emptyLabel ?? 'No frames yet.'}
        </p>
      </div>
    );
  }
  return (
    <div className={styles.framesList} style={maxHeight ? { maxHeight } : undefined}>
      {pairs.map((p, idx) => (
        <PairedRow key={`${p.messageId}|${p.request.timestamp}|${idx}`} pair={p} />
      ))}
    </div>
  );
}
